import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
} from '@mui/material';
import { SurfaceCard } from '../ui/SurfaceCard';
import { Metric } from '../ui/Metric';
import { ChartLegend, ChartTooltipFrame, axisTick } from './ChartCard';
import { formatCompactNumber, formatTokenAmount, formatUsd } from '../../utils/formatters';
import { tokens } from '../../theme';
import { format, startOfDay, startOfWeek, startOfMonth, subDays, subMonths, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval } from 'date-fns';
import { IBuyBack } from '../../shared/types';

type TimeRange = 'daily' | 'weekly' | 'monthly' | 'all';
type DataPeriod = '7d' | '30d' | '90d' | 'all';

interface TradingDataPoint {
  period: number;
  periodLabel: string;
  bought: number;  // RIF comprados (dipster)
  sold: number;    // RIF vendidos (dumper)
  buyValue: number;  // USD gastados en compras
  sellValue: number; // USD recibidos en ventas
}

interface TradingStatsChartProps {
  completedTasks: IBuyBack[];
  pendingTasks: IBuyBack[];
  sx?: object;
}

const formatNumber = (num: number, decimals = 0) =>
  num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

const formatCurrency = (num: number) =>
  `$${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const formatAveragePrice = (num: number | null) =>
  num === null
    ? '—'
    : `$${num.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}`;

export const calculateAverageBuyPrice = (tasks: IBuyBack[]): number | null => {
  const buyTasks = tasks.filter(t => t.taskType === 'RIF_BUY');
  const totalBought = buyTasks.reduce((sum, t) => sum + (t.totalBaseBought || 0), 0);
  const totalSpent = buyTasks.reduce((sum, t) => sum + (t.totalQuoteSpent || 0), 0);
  return totalBought > 0 ? totalSpent / totalBought : null;
};

const TradingStatsChart: React.FC<TradingStatsChartProps> = ({
  completedTasks,
  pendingTasks,
  sx,
}) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('daily');
  const [dataPeriod, setDataPeriod] = useState<DataPeriod>('30d');

  // Combine all tasks and categorize by type
  const allTasks = useMemo(() => [...completedTasks, ...pendingTasks], [completedTasks, pendingTasks]);

  // Weighted across every executed RIF purchase, independent of the chart period selector.
  const averageBuyPrice = useMemo(() => calculateAverageBuyPrice(allTasks), [allTasks]);

  // Summary stats follow the period selector (same tasks the chart plots).
  const summaryStats = useMemo(() => {
    const days = dataPeriod === '7d' ? 7 : dataPeriod === '30d' ? 30 : dataPeriod === '90d' ? 90 : null;
    const from = days ? subDays(new Date(), days).getTime() : -Infinity;
    const inPeriod = allTasks.filter(t => (t.lastUpdatedAt ?? 0) >= from);
    const buyTasks = inPeriod.filter(t => t.taskType !== 'RIF_SELL');
    const sellTasks = inPeriod.filter(t => t.taskType === 'RIF_SELL');

    const totalBought = buyTasks.reduce((sum, t) => sum + (t.totalBaseBought || 0), 0);
    const totalSold = sellTasks.reduce((sum, t) => sum + (t.totalBaseBought || 0), 0); // totalBaseBought = sold for sell tasks
    const totalSpentBuying = buyTasks.reduce((sum, t) => sum + (t.totalQuoteSpent || 0), 0);
    const totalReceivedSelling = sellTasks.reduce((sum, t) => sum + (t.totalQuoteSpent || 0), 0); // totalQuoteSpent = received for sell tasks

    return {
      totalBought,
      totalSold,
      totalSpentBuying,
      totalReceivedSelling,
      netPosition: totalBought - totalSold,
    };
  }, [allTasks, dataPeriod]);
  const periodLabel = { '7d': '1W', '30d': '1M', '90d': '3M', all: 'all time' }[dataPeriod as string] ?? '';

  // Process data into time buckets
  const chartData = useMemo(() => {
    const now = new Date();
    let startDate: Date;
    let intervals: Date[];
    let formatFn: (d: Date) => string;

    // Determine date range based on period
    switch (dataPeriod) {
      case '7d':
        startDate = subDays(now, 7);
        break;
      case '30d':
        startDate = subDays(now, 30);
        break;
      case '90d':
        startDate = subDays(now, 90);
        break;
      default:
        // Find earliest task
        const allUpdates = allTasks.map(t => t.lastUpdatedAt).filter(Boolean);
        startDate = allUpdates.length > 0 
          ? new Date(Math.min(...allUpdates))
          : subMonths(now, 6);
    }

    // Generate intervals based on time range
    switch (timeRange) {
      case 'daily':
        intervals = eachDayOfInterval({ start: startDate, end: now });
        formatFn = (d) => format(d, 'MMM d');
        break;
      case 'weekly':
        // Monday weeks, matching the bucket keys below (a Sunday start dropped the current week).
        intervals = eachWeekOfInterval({ start: startDate, end: now }, { weekStartsOn: 1 });
        formatFn = (d) => format(d, "'W'I MMM");
        break;
      case 'monthly':
        intervals = eachMonthOfInterval({ start: startDate, end: now });
        formatFn = (d) => format(d, 'MMM yyyy');
        break;
      default:
        intervals = eachMonthOfInterval({ start: startDate, end: now });
        formatFn = (d) => format(d, 'MMM yyyy');
    }

    // Bucket tasks into intervals
    const getIntervalStart = (date: Date): number => {
      switch (timeRange) {
        case 'daily':
          return startOfDay(date).getTime();
        case 'weekly':
          return startOfWeek(date, { weekStartsOn: 1 }).getTime();
        case 'monthly':
        default:
          return startOfMonth(date).getTime();
      }
    };

    // Initialize buckets
    const buckets = new Map<number, TradingDataPoint>();
    intervals.forEach((date) => {
      const t = getIntervalStart(date);
      buckets.set(t, {
        period: t,
        periodLabel: formatFn(date),
        bought: 0,
        sold: 0,
        buyValue: 0,
        sellValue: 0,
      });
    });

    // Fill buckets with task data — only tasks inside the selected period, like the summary tiles.
    allTasks.forEach((task) => {
      if (!task.lastUpdatedAt || task.lastUpdatedAt < startDate.getTime()) return;
      const taskDate = new Date(task.lastUpdatedAt);
      const bucketTime = getIntervalStart(taskDate);
      const bucket = buckets.get(bucketTime);
      
      if (bucket) {
        const isBuy = task.taskType !== 'RIF_SELL';
        if (isBuy) {
          bucket.bought += task.totalBaseBought || 0;
          bucket.buyValue += task.totalQuoteSpent || 0;
        } else {
          bucket.sold += task.totalBaseBought || 0;
          bucket.sellValue += task.totalQuoteSpent || 0;
        }
      }
    });

    // Convert to array and calculate cumulative
    const dataArray = Array.from(buckets.values()).sort((a, b) => a.period - b.period);
    
    let cumBought = 0;
    let cumSold = 0;
    return dataArray.map(point => {
      cumBought += point.bought;
      cumSold += point.sold;
      return {
        ...point,
        cumulativeBought: cumBought,
        cumulativeSold: cumSold,
      };
    });
  }, [allTasks, timeRange, dataPeriod]);

  if (!allTasks.length) {
    return (
      <SurfaceCard sx={{ textAlign: 'center', ...sx }}>
        <Typography color="text.secondary">No trading data available</Typography>
      </SurfaceCard>
    );
  }

  const legend = [
    { label: 'Bought (Dipster)', color: tokens.chart.green, shape: 'rect' as const },
    { label: 'Sold (Dumper)', color: tokens.chart.gold, shape: 'rect' as const },
  ];

  const TradingTooltip: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload as TradingDataPoint & { cumulativeBought: number; cumulativeSold: number };
    const row = (name: string, color: string, value: string, sub?: string) => (
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: color }} />
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{name}</Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {value}
          {sub && (
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.75 }}>
              {sub}
            </Box>
          )}
        </Typography>
      </Stack>
    );
    return (
      <ChartTooltipFrame minWidth={240}>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 650, mb: 1 }}>{label}</Typography>
        <Stack spacing={0.75}>
          {row('Bought', tokens.chart.green, formatNumber(point.bought), formatCurrency(point.buyValue))}
          {row('Sold', tokens.chart.gold, formatNumber(point.sold), formatCurrency(point.sellValue))}
        </Stack>
        <Box sx={{ my: 1, borderTop: `1px solid ${tokens.border}` }} />
        <Stack spacing={0.75}>
          {row('Cumulative bought', tokens.chart.green, formatNumber(point.cumulativeBought))}
          {row('Cumulative sold', tokens.chart.gold, formatNumber(point.cumulativeSold))}
        </Stack>
      </ChartTooltipFrame>
    );
  };

  const xAxisProps = {
    dataKey: 'periodLabel',
    tick: axisTick,
    axisLine: false,
    tickLine: false,
    interval: 'preserveStartEnd' as const,
    minTickGap: 24,
  };

  return (
    <SurfaceCard sx={sx}>
      {/* Header with Controls */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={{ xs: 2.5, md: 3 }}
      >
        <Box>
          <Typography component="h3" sx={{ fontSize: { xs: '0.9375rem', md: '1rem' }, fontWeight: 650 }}>
            Trading activity
          </Typography>
          <Typography variant="caption" color="text.secondary">
            RIF bought (Dipster) vs sold (Dumper) over time
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <ToggleButtonGroup
            value={dataPeriod}
            exclusive
            onChange={(_, v) => v && setDataPeriod(v)}
            size="small"
            aria-label="Period"
          >
            <ToggleButton value="7d">1W</ToggleButton>
            <ToggleButton value="30d">1M</ToggleButton>
            <ToggleButton value="90d">3M</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={(_, v) => v && setTimeRange(v)}
            size="small"
            aria-label="Granularity"
          >
            <ToggleButton value="daily">Daily</ToggleButton>
            <ToggleButton value="weekly">Weekly</ToggleButton>
            <ToggleButton value="monthly">Monthly</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* Summary Stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, max-content))' },
          columnGap: { xs: 2, sm: 5 },
          rowGap: 2,
          mb: 3,
        }}
      >
        <Metric
          label={`RIF bought · ${periodLabel}`}
          value={formatTokenAmount(summaryStats.totalBought)}
          sub={summaryStats.totalSpentBuying ? `${formatUsd(summaryStats.totalSpentBuying)} spent` : "—"}
          size="lg"
        />
        <Metric
          label="Avg buy price"
          value={formatAveragePrice(averageBuyPrice)}
          sub="weighted · all time"
          size="lg"
        />
        <Metric
          label={`RIF sold · ${periodLabel}`}
          value={formatTokenAmount(summaryStats.totalSold)}
          sub={summaryStats.totalReceivedSelling ? `${formatUsd(summaryStats.totalReceivedSelling)} received` : "—"}
          size="lg"
        />
        <Metric
          label={`Net position · ${periodLabel}`}
          value={`${summaryStats.netPosition >= 0 ? '+' : ''}${formatTokenAmount(summaryStats.netPosition)}`}
          sub="RIF"
          tone="auto"
          signal={summaryStats.netPosition}
          size="lg"
        />
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary">Cumulative</Typography>
        <ChartLegend items={legend} />
      </Stack>
      <Box sx={{ height: { xs: 180, md: 220 }, width: '100%', ml: -1 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} syncId="trading-activity" margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={tokens.chart.grid} />
            <XAxis {...xAxisProps} hide />
            <YAxis tick={axisTick} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => formatCompactNumber(v)} />
            <Tooltip content={<TradingTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.22)', strokeWidth: 1 }} />
            <Line type="monotone" dataKey="cumulativeBought" stroke={tokens.chart.green} strokeWidth={2} dot={false} activeDot={{ r: 4, stroke: tokens.card, strokeWidth: 2 }} />
            <Line type="monotone" dataKey="cumulativeSold" stroke={tokens.chart.gold} strokeWidth={2} dot={false} activeDot={{ r: 4, stroke: tokens.card, strokeWidth: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Typography variant="overline" color="text.secondary" component="div" sx={{ mt: 2, mb: 1 }}>
        Per {timeRange === 'daily' ? 'day' : timeRange === 'weekly' ? 'week' : 'month'}
      </Typography>
      <Box sx={{ height: { xs: 140, md: 160 }, width: '100%', ml: -1 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} syncId="trading-activity" margin={{ top: 4, right: 12, left: 0, bottom: 0 }} barGap={2}>
            <CartesianGrid vertical={false} stroke={tokens.chart.grid} />
            <XAxis {...xAxisProps} />
            <YAxis tick={axisTick} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => formatCompactNumber(v)} />
            <Tooltip content={<TradingTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Bar dataKey="bought" fill={tokens.chart.green} radius={[3, 3, 0, 0]} maxBarSize={12} />
            <Bar dataKey="sold" fill={tokens.chart.gold} radius={[3, 3, 0, 0]} maxBarSize={12} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </SurfaceCard>
  );
};

export default TradingStatsChart;
